import { confirm } from 'components/modals/confirm'
import {
  convertDirtyFieldsStatusToModalItems,
  DirtyFieldsModal,
  DirtyFieldsModalConfig,
  DirtyFieldsObject,
  getDirtyFieldsObject,
} from 'components/modals/fields-summary'
import { LabelMap } from 'util/fields-labels/model-fieldsLabels'
import { MetaRoot } from 'util/metaPath'
import { isObjectDeepEmpty } from 'util/object'
import { soapFieldPaths } from 'view/atendimentos/detail/soap/fields-summary-modal/soapFormLabels'

import { AlertaRetificacaoAtendimentoText } from './AlertaRetificacaoAtendimentoText'
import { RetificacaoConfirmModalOptions } from './model-retificacaoAtendimento'
import { confirmarEdicaoTitleMessage } from './util-retificacaoMessages'

const IGNORED_FIELDS = ['_id', 'openedAccordions']

export interface RetificacaoAtendimentoConfirmModalOptions<FormModel, RetificarMutationResult>
  extends RetificacaoConfirmModalOptions<FormModel, RetificarMutationResult> {
  labels: LabelMap<FormModel>
  config: DirtyFieldsModalConfig<FormModel>
  meta: MetaRoot<FormModel>
}

export function retificacaoAtendimentoConfirmModal<FormModel, RetificarMutationResult>(
  options: Readonly<RetificacaoAtendimentoConfirmModalOptions<FormModel, RetificarMutationResult>>
) {
  const { onRetificarSemAlteracoes, initialValues, currentValues, ...rest } = options

  const dirtyFieldsObject = getDirtyFieldsObject(initialValues, currentValues, soapFieldPaths, IGNORED_FIELDS)

  return isObjectDeepEmpty(dirtyFieldsObject)
    ? modalSemAlteracoes({ onRetificarSemAlteracoes })
    : modalComAlteracoes(rest, dirtyFieldsObject)
}

export async function modalSegundaVerificacao<FormModel, RetificarMutationResult>(
  options: Pick<
    RetificacaoAtendimentoConfirmModalOptions<FormModel, RetificarMutationResult>,
    'nomeCidadao' | 'onRetificar' | 'tipoAtendimentoProfissional'
  >
) {
  const { nomeCidadao, onRetificar, tipoAtendimentoProfissional } = options

  const confirmed = await confirm({
    title: confirmarEdicaoTitleMessage(tipoAtendimentoProfissional, nomeCidadao),
    icon: {
      name: 'infoCircleOutline',
      fill: 'primary',
    },
    render: () => (
      <AlertaRetificacaoAtendimentoText
        tipoAtendimentoProfissional={tipoAtendimentoProfissional}
        momento='confirmacaoEdicao'
      />
    ),
  })()

  if (confirmed) {
    return onRetificar()
  }
}

export async function modalSemAlteracoes<FormModel, RetificarMutationResult>(
  options: Pick<
    RetificacaoAtendimentoConfirmModalOptions<FormModel, RetificarMutationResult>,
    'onRetificarSemAlteracoes'
  >
) {
  const { onRetificarSemAlteracoes } = options

  const confirmed = await confirm({
    title: 'Deseja finalizar sem retificar?',
    confirmLabel: 'Finalizar sem retificar',
    icon: {
      name: 'questionMarkOutline',
      fill: 'normal',
    },
    cancelLabel: 'Não, continuar aqui',
  })()

  if (confirmed) {
    return onRetificarSemAlteracoes()
  }
}

async function modalComAlteracoes<FormModel, RetificarMutationResult>(
  options: Omit<
    RetificacaoAtendimentoConfirmModalOptions<FormModel, RetificarMutationResult>,
    'onRetificarSemAlteracoes' | 'initialValues' | 'currentValues'
  >,
  dirtyFields: DirtyFieldsObject<FormModel>
) {
  const { labels, config, meta, ...rest } = options

  const confirmed = await confirm({
    title: 'Os seguintes registros serão retificados:',
    icon: {
      name: 'exclamationTriangleOutline',
      fill: 'alert',
    },
    size: 'auto',
    render: () => <DirtyFieldsModal items={convertDirtyFieldsStatusToModalItems(dirtyFields, labels, config, meta)} />,
  })()

  if (confirmed) {
    return modalSegundaVerificacao(rest)
  }
}
